'use client';

/**
 * @brisa/ui · facelift/club-map-marker/ClubMapMarkerMiniCard
 *
 * Feature: migracao-facelift-v1 · ADR §D6 §"Sub-tabela RSC"
 * Figma origem: node 183:1047 (= master 183:851 / instance 183:1024 — ajustado
 * pelo PL em 2026-05-15 após Pass 2 do DESIGN_AGENT).
 *
 * Layout final aprovado pelo PL:
 *   - Container w-80 bg-white rounded-[var(--radius-md)] (16px) com shadow stack
 *   - Photo block h-[180px] com bg #454D4D placeholder ou cover_url, scrim gradient
 *   - Avatar 40×40 top-left (AvatarClube com drop-shadow próprio)
 *   - Verified badge "B" 28×28 top-right (apenas se partner)
 *   - Vibe tags translúcidas (bg-white/16) no bottom da foto — até 2 + "+N"
 *   - Body p-4 col gap-4: nome + pin row (MapPin lucide) + CTA primary full-width
 *   - CTA "Lista de espera" (FLP=OFF) / "Em breve" (FLP=ON) — primary amarelo + Plus icon
 *
 * 'use client' obrigatório (F1-CMM-02, F1-RSC-08).
 */
import * as React from 'react';
import { MapPin, Plus } from 'lucide-react';

import { AvatarClube } from '../avatar-clube/AvatarClube';
import { Button } from '../../ui/button';
import { cn } from '@brisa/ui/lib/utils';

export interface ClubMapMarkerMiniCardClub {
  slug: string;
  name: string;
  neighborhood?: string | null;
  cover_url?: string | null;
  vibe?: string;
  /** Múltiplas vibes — até 2 são mostradas + indicador "+N" se houver mais. */
  vibes?: string[];
  integration_status?: 'partner' | 'listed' | string;
}

export interface ClubMapMarkerMiniCardProps extends React.HTMLAttributes<HTMLDivElement> {
  club: ClubMapMarkerMiniCardClub;
  surface: string;
  flpEnabled: boolean;
}

// Shadow stack do Figma 183:1024 (5 camadas — top-anchored popover)
const SHADOW_STACK =
  '[filter:drop-shadow(0_141px_56px_rgba(0,0,0,0.01))_drop-shadow(0_79px_48px_rgba(0,0,0,0.05))_drop-shadow(0_35px_35px_rgba(0,0,0,0.09))_drop-shadow(0_9px_19px_rgba(0,0,0,0.10))]';

/**
 * Selo "B" parceiro — replicado do `163:919` Verified component do Figma.
 * Mesmo path do ClubCardFacelift (verified.svg oficial do PL).
 */
function VerifiedPartnerBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={28}
      height={28}
      fill="none"
      aria-label="Club parceiro"
      role="img"
      data-slot="partner-badge"
      className="shrink-0"
    >
      <path
        d="M11.4668 30L8.9335 25.7333L4.1335 24.6667L4.60016 19.7333L1.3335 16L4.60016 12.2667L4.1335 7.33333L8.9335 6.26667L11.4668 2L16.0002 3.93333L20.5335 2L23.0668 6.26667L27.8668 7.33333L27.4002 12.2667L30.6668 16L27.4002 19.7333L27.8668 24.6667L23.0668 25.7333L20.5335 30L16.0002 28.0667L11.4668 30Z"
        fill="#FFE400"
      />
      <path
        d="M11.9619 20.4999V11.5559H17.9159C18.3666 11.5559 18.7782 11.6512 19.1509 11.8419C19.5236 12.0239 19.8226 12.2796 20.0479 12.6089C20.2732 12.9382 20.3859 13.3196 20.3859 13.7529C20.3859 14.1862 20.3166 14.5416 20.1779 14.8189C20.0392 15.0962 19.8572 15.3172 19.6319 15.4819C19.4066 15.6379 19.1596 15.7549 18.8909 15.8329V15.8849C19.2029 15.9542 19.4846 16.0756 19.7359 16.2489C19.9959 16.4136 20.2039 16.6476 20.3599 16.9509C20.5159 17.2542 20.5939 17.6442 20.5939 18.1209C20.5939 18.5976 20.4769 19.0136 20.2429 19.3689C20.0089 19.7242 19.6969 20.0016 19.3069 20.2009C18.9169 20.4002 18.4792 20.4999 17.9939 20.4999H11.9619ZM14.8349 18.4199H17.0189C17.2182 18.4199 17.3786 18.3506 17.4999 18.2119C17.6212 18.0732 17.6819 17.8652 17.6819 17.5879C17.6819 17.4579 17.6516 17.3409 17.5909 17.2369C17.5389 17.1242 17.4609 17.0376 17.3569 16.9769C17.2616 16.9162 17.1489 16.8859 17.0189 16.8859H14.8349V18.4199ZM14.8349 15.0399H16.8109C16.9496 15.0399 17.0666 15.0096 17.1619 14.9489C17.2572 14.8796 17.3309 14.7842 17.3829 14.6629C17.4436 14.5416 17.4739 14.3899 17.4739 14.2079C17.4739 14.0172 17.4089 13.8569 17.2789 13.7269C17.1489 13.5882 16.9929 13.5189 16.8109 13.5189H14.8349V15.0399Z"
        fill="black"
      />
    </svg>
  );
}

/**
 * Resolve a lista de vibes: prioridade pra `vibes` array; fallback pra `[vibe]`.
 * Mostra até 2 + indicador "+N" se houver mais.
 */
function resolveVibes(club: ClubMapMarkerMiniCardClub): {
  visible: string[];
  overflow: number;
} {
  const all = club.vibes && club.vibes.length > 0
    ? club.vibes
    : club.vibe
      ? [club.vibe]
      : [];
  const visible = all.slice(0, 2);
  const overflow = Math.max(0, all.length - 2);
  return { visible, overflow };
}

export function ClubMapMarkerMiniCard({
  club,
  surface,
  flpEnabled,
  className,
  ...props
}: ClubMapMarkerMiniCardProps) {
  const isPartner = club.integration_status === 'partner';
  const ctaLabel = flpEnabled ? 'Em breve' : 'Lista de espera';
  const { visible: visibleVibes, overflow: vibeOverflow } = resolveVibes(club);

  return (
    <div
      className={cn(
        'flex w-80 flex-col items-start overflow-hidden rounded-[var(--radius-md)] bg-[hsl(var(--neutral-white))]',
        SHADOW_STACK,
        className,
      )}
      data-slot="club-map-mini-card"
      data-surface={surface}
      {...props}
    >
      {/* Photo block */}
      <div
        className="relative h-[180px] w-full overflow-hidden bg-[#454D4D]"
        data-slot="photo"
      >
        {club.cover_url ? (
          <img
            src={club.cover_url}
            alt={`Foto de ${club.name}`}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}

        {/* Scrim gradient (mesmo do ClubCardFacelift — transparent até 46% depois black) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-[46.6%] to-black/60"
        />

        {/* Avatar top-left (40×40) */}
        <AvatarClube
          club={club}
          size="md"
          className="absolute left-4 top-3"
        />

        {/* Verified badge top-right (28×28) — apenas se partner */}
        {isPartner ? (
          <div className="absolute right-4 top-4">
            <VerifiedPartnerBadge />
          </div>
        ) : null}

        {/* Vibe tags (até 2 + "+N") no bottom da foto */}
        {visibleVibes.length > 0 ? (
          <div
            className="absolute bottom-4 left-4 flex items-center gap-2"
            data-slot="vibe-tags"
          >
            {visibleVibes.map((v) => (
              <span
                key={v}
                className="inline-flex items-center justify-center rounded-full bg-white/[0.16] px-3 py-2 text-xs font-semibold leading-none text-white/[0.72]"
              >
                {v}
              </span>
            ))}
            {vibeOverflow > 0 ? (
              <span className="inline-flex items-center justify-center rounded-full bg-white/[0.16] px-3 py-2 text-xs font-semibold leading-none text-white/[0.72]">
                +{vibeOverflow}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="flex w-full flex-col items-start gap-4 p-4" data-slot="body">
        {/* Info group */}
        <div className="flex w-full flex-col items-start gap-1">
          <h4 className="font-body text-lg font-medium leading-tight text-[hsl(var(--text-100))]">
            {club.name}
          </h4>
          {club.neighborhood ? (
            <div className="flex items-center gap-1" data-slot="neighborhood-row">
              <MapPin size={12} className="shrink-0 text-[hsl(var(--text-64))]" aria-hidden="true" />
              <span className="text-xs text-[hsl(var(--text-64))]">
                {club.neighborhood}
              </span>
            </div>
          ) : null}
        </div>

        {/* Action footer — CTA primary full-width */}
        <div className="flex w-full items-start" data-slot="action-footer">
          <Button
            variant="primary-medium"
            size="md"
            className="w-full gap-2 text-xs"
            data-slot={flpEnabled ? 'cta-em-breve' : 'cta-lista-espera'}
          >
            {ctaLabel}
            <Plus size={16} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
